import { useState, useRef, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  Crown,
  TrendingUp,
  Gift,
  RefreshCw,
  CheckCircle,
  Info,
  Copy,
  HelpCircle,
  BarChart2,
  Share2,
  QrCode,
  Download,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import html2canvas from "html2canvas";
import { formatNumber, formatUSDT } from "@/lib/mockData";
import {
  formatAddress,
  formatEtherWithDecimals,
  formatKMBTruncate,
} from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useUserStore } from "@/store/useUserStore";
import { useUserInviteLevel } from "@/hooks/api/useUserInviteLevel";
import { useInviteLevelConfig } from "@/hooks/api/useInviteLevelConfig";
import { useUserInviteTeam } from "@/hooks/api/useUserInviteTeam";
import type { InviteLevelConfigItem } from "@/api/types";
import { copyToClipboard } from "@/lib/clipboard";
import { showError, showInfo, showSuccess } from "@/lib/toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getTokenName, INTEGRAL_COIN_CONTRACT } from "@/config/common";
import { CCC_AIF_ADDRESS, USDT_ADDRESS } from "@/config/env";
import { formatEther } from "viem";

const C = {
  bg: "#0a0b0e",
  card: "#0f1117",
  card2: "#161a22",
  border: "rgba(255,255,255,0.07)",
  text: "#e2e8f0",
  muted: "#94a3b8",
  dim: "#64748b",
  primary: "#22c55e",
  green: "#22c55e",
  red: "#ef4444",
  amber: "#f59e0b",
};

function findNextInviteLevelConfig(
  items: InviteLevelConfigItem[] | undefined,
  coinContract: string,
  currentLevel: number
): InviteLevelConfigItem | undefined {
  if (!items?.length || !coinContract) return undefined;
  const c = coinContract.toLowerCase();
  const want = currentLevel + 1;
  return items.find(
    row => row.coinContract.toLowerCase() === c && row.level === want
  );
}

/** 各等级 C1~C10 颜色映射（与 dapp 等级参数表一致） */
const LEVEL_COLORS: Record<number, string> = {
  1: "#6b7280", // 初级 - 灰
  2: "#a78bfa", // 青铜 - 紫
  3: "#94a3b8", // 白银 - 银
  4: "#fbbf24", // 黄金 - 金
  5: "#22c55e", // 铂金 - 绿
  6: "#60a5fa", // 钻石 - 蓝
  7: "#f59e0b", // 皇冠 - 琥珀
  8: "#ef4444", // 王者 - 红
  9: "#ec4899", // 传奇 - 玫红
  10: "#d946ef", // 至尊 - 紫红
};
const getLevelColor = (level: number): string =>
  LEVEL_COLORS[level] ?? "#6b7280";

const TIER_I18N_KEYS = [
  "",
  "teamPage.tierL1",
  "teamPage.tierL2",
  "teamPage.tierL3",
  "teamPage.tierL4",
  "teamPage.tierL5",
  "teamPage.tierL6",
  "teamPage.tierL7",
  "teamPage.tierL8",
  "teamPage.tierL9",
  "teamPage.tierL10",
] as const;

function parseWeiToUsdNum(raw: string | undefined): number {
  try {
    return parseFloat(formatEther(BigInt(raw ?? "0")));
  } catch {
    return 0;
  }
}

/** 弹窗/副标题：个人业绩列展示（链上最小单位 → 美元） */
function formatInviteLevelPersonalCell(
  wei: string | undefined,
  t: (key: string) => string
): string {
  const n = parseWeiToUsdNum(wei);
  if (n <= 0) return t("teamPage.levelThresholdPersonalNone");
  return `$${formatUSDT(n)}`;
}

/** 弹窗/副标题：团队业绩列展示 */
function formatInviteLevelTeamCell(wei: string | undefined): string {
  const n = parseWeiToUsdNum(wei);
  if (n <= 0) return "—";
  return `$${formatNumber(n)}`;
}

function tierNameForLevel(level: number, t: (key: string) => string): string {
  if (level < 1 || level > 10) return t("teamPage.tierUnknown");
  return t(TIER_I18N_KEYS[level]);
}

// ===== 等级信息弹窗（无直推/团队奖励/平级列；数据来自 GetInviteLevelConfig） =====
function LevelInfoDialog({
  open,
  onClose,
  currentLevel,
  rows,
  isLoading,
}: {
  open: boolean;
  onClose: () => void;
  currentLevel: number;
  rows: InviteLevelConfigItem[];
  isLoading: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-md max-h-[85vh] overflow-y-auto p-0 gap-0"
        style={{
          background: "#0d0f14",
          border: `1px solid ${C.border}`,
          color: C.text,
        }}
      >
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="text-sm font-bold" style={{ color: C.text }}>
            {t("teamPage.levelTableTitle")}
          </DialogTitle>
        </DialogHeader>
        <div className="px-4 pb-4">
          {isLoading ? (
            <div
              className="rounded-xl py-10 text-center text-[11px]"
              style={{ border: `1px solid ${C.border}`, color: C.dim }}
            >
              {t("teamPage.levelTableLoading")}
            </div>
          ) : rows.length === 0 ? (
            <div
              className="rounded-xl py-10 text-center text-[11px]"
              style={{ border: `1px solid ${C.border}`, color: C.dim }}
            >
              {t("teamPage.levelTableEmpty")}
            </div>
          ) : (
            <div
              className="overflow-x-auto rounded-xl"
              style={{ border: `1px solid ${C.border}` }}
            >
              <table
                className="w-full text-[10px]"
                style={{ borderCollapse: "separate", borderSpacing: 0 }}
              >
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.04)" }}>
                    <th
                      className="py-2 px-1.5 text-center font-bold whitespace-nowrap"
                      style={{
                        color: C.muted,
                        borderBottom: `1px solid ${C.border}`,
                      }}
                    >
                      {t("teamPage.levelColLevel")}
                    </th>
                    <th
                      className="py-2 px-1.5 text-center font-bold whitespace-nowrap"
                      style={{
                        color: C.muted,
                        borderBottom: `1px solid ${C.border}`,
                      }}
                    >
                      {t("teamPage.levelColPersonal")}
                    </th>
                    <th
                      className="py-2 px-1.5 text-center font-bold whitespace-nowrap"
                      style={{
                        color: C.muted,
                        borderBottom: `1px solid ${C.border}`,
                      }}
                    >
                      {t("teamPage.levelColTeam")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => {
                    const id = `C${row.level}`;
                    const active = row.level === currentLevel;
                    const personal = formatInviteLevelPersonalCell(
                      row.userYjAmount,
                      t
                    );
                    const team = formatInviteLevelTeamCell(row.teamYjAmount);
                    return (
                      <tr
                        key={row.id || id}
                        style={{
                          background:
                            i % 2 === 1
                              ? "rgba(255,255,255,0.015)"
                              : "transparent",
                          borderBottom:
                            i < rows.length - 1
                              ? `1px solid ${C.border}`
                              : "none",
                        }}
                      >
                        <td
                          className="py-2 px-1.5 text-center"
                          style={{
                            background: active
                              ? "rgba(34,197,94,0.06)"
                              : undefined,
                          }}
                        >
                          <span
                            className="font-bold"
                            style={{ color: getLevelColor(row.level) }}
                          >
                            {id}
                          </span>
                          <div className="text-[9px]" style={{ color: C.dim }}>
                            {tierNameForLevel(row.level, t)}
                          </div>
                        </td>
                        <td
                          className="py-2 px-1.5 text-center mono-num"
                          style={{
                            color: C.text,
                            background: active
                              ? "rgba(34,197,94,0.06)"
                              : undefined,
                          }}
                        >
                          {personal}
                        </td>
                        <td
                          className="py-2 px-1.5 text-center mono-num"
                          style={{
                            color: C.text,
                            background: active
                              ? "rgba(34,197,94,0.06)"
                              : undefined,
                          }}
                        >
                          {team}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div
            className="text-[10px] mt-2 space-y-1 pt-1"
            style={{ color: C.dim }}
          >
            <p>• {t("teamPage.levelFooterPersonal")}</p>
            <p>• {t("teamPage.levelFooterTeam")}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===== 领取奖励弹窗（布局与 dapp 一致；数值占位为 -；开放前由外层按钮提示未开放） =====
function ConfettiCanvas({ active }: { active: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (!active || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);
    const W = canvas.offsetWidth;
    const H = canvas.offsetHeight;
    const colors = [
      "#22c55e",
      "#fbbf24",
      "#60a5fa",
      "#a78bfa",
      "#ec4899",
      "#f97316",
      "#14b8a6",
    ];
    const particles: {
      x: number;
      y: number;
      vx: number;
      vy: number;
      w: number;
      h: number;
      rot: number;
      vr: number;
      color: string;
      life: number;
    }[] = [];
    for (let i = 0; i < 80; i++) {
      particles.push({
        x: W / 2 + (Math.random() - 0.5) * 60,
        y: H * 0.4,
        vx: (Math.random() - 0.5) * 8,
        vy: -Math.random() * 10 - 3,
        w: Math.random() * 6 + 3,
        h: Math.random() * 4 + 2,
        rot: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 0.3,
        color: colors[Math.floor(Math.random() * colors.length)],
        life: 1,
      });
    }
    let raf = 0;
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      let alive = false;
      for (const p of particles) {
        if (p.life <= 0) continue;
        alive = true;
        p.vy += 0.18;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        p.life -= 0.008;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.globalAlpha = Math.max(0, p.life);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }
      if (alive) raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [active]);
  if (!active) return null;
  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 50 }}
    />
  );
}

function ClaimRewardDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState<
    "form" | "countdown" | "pending" | "success"
  >("form");
  const [countdown, setCountdown] = useState(3);
  const handleClose = () => {
    setStep("form");
    setCountdown(3);
    onClose();
  };

  useEffect(() => {
    if (step !== "countdown") return;
    if (countdown <= 0) {
      setStep("pending");
      const tmr = window.setTimeout(() => setStep("success"), 1500);
      return () => window.clearTimeout(tmr);
    }
    const tm = window.setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => window.clearTimeout(tm);
  }, [step, countdown]);

  const dash = "-";
  const tokenUsdt = getTokenName(USDT_ADDRESS);
  const tokenTsk = getTokenName(CCC_AIF_ADDRESS);
  const tokenTskx = getTokenName(INTEGRAL_COIN_CONTRACT);

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next) handleClose();
      }}
    >
      <DialogContent
        className="max-w-sm"
        style={{
          background: "#0d0f14",
          border: "1px solid rgba(34,197,94,0.20)",
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: C.primary }}>
            {t("teamPage.claimDialogTitle")}
          </DialogTitle>
        </DialogHeader>
        <AnimatePresence mode="wait">
          {step === "form" && (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-4"
            >
              <div
                className="rounded-lg p-4"
                style={{ background: C.card, border: `1px solid ${C.border}` }}
              >
                <div
                  className="text-[10px] uppercase tracking-wider mb-3"
                  style={{ color: C.dim }}
                >
                  {t("teamPage.claimPendingRewardsLabel")}
                </div>
                <div className="space-y-3">
                  {[
                    {
                      label: tokenUsdt,
                      suffix: t("teamPage.rewardTokenSuffixStable"),
                      value: dash,
                      color: C.green,
                      bg: "rgba(34,197,94,0.08)",
                      border: "rgba(34,197,94,0.20)",
                    },
                    {
                      label: tokenTsk,
                      suffix: t("teamPage.rewardTokenSuffixToken"),
                      value: dash,
                      color: C.primary,
                      bg: "rgba(34,197,94,0.08)",
                      border: "rgba(34,197,94,0.20)",
                    },
                    {
                      label: tokenTskx,
                      suffix: t("teamPage.rewardTokenSuffixPoints"),
                      value: dash,
                      color: C.amber,
                      bg: "rgba(245,158,11,0.08)",
                      border: "rgba(245,158,11,0.20)",
                    },
                  ].map(item => (
                    <div
                      key={item.label}
                      className="flex items-center justify-between rounded-lg px-3 py-2.5"
                      style={{
                        background: item.bg,
                        border: `1px solid ${item.border}`,
                      }}
                    >
                      <span className="text-[10px]" style={{ color: C.muted }}>
                        {item.label}
                        <span className="ml-1 opacity-60">({item.suffix})</span>
                      </span>
                      <span
                        className="mono-num font-bold text-xs"
                        style={{ color: item.color }}
                      >
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div
                className="rounded-lg p-3 text-[10px] space-y-1.5"
                style={{
                  background: "rgba(34,197,94,0.04)",
                  border: "1px solid rgba(34,197,94,0.12)",
                }}
              >
                {[
                  t("teamPage.claimCreditLine1"),
                  t("teamPage.claimCreditLine2"),
                  t("teamPage.claimCreditLine3"),
                ].map((line, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-1.5"
                    style={{ color: C.muted }}
                  >
                    <div
                      className="w-1 h-1 rounded-full shrink-0"
                      style={{ background: C.primary }}
                    />
                    {line}
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="w-full py-3 rounded-lg font-bold text-xs btn-primary"
                onClick={() => {
                  setCountdown(3);
                  setStep("countdown");
                }}
              >
                {t("teamPage.claimConfirmAll")}
              </button>
            </motion.div>
          )}
          {step === "countdown" && (
            <motion.div
              key="countdown"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center py-8 gap-4"
            >
              <div className="relative w-20 h-20">
                <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
                  <circle
                    cx="40"
                    cy="40"
                    r="36"
                    fill="none"
                    stroke="rgba(255,255,255,0.06)"
                    strokeWidth="4"
                  />
                  <motion.circle
                    cx="40"
                    cy="40"
                    r="36"
                    fill="none"
                    stroke={C.primary}
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 36}
                    initial={{ strokeDashoffset: 0 }}
                    animate={{ strokeDashoffset: 2 * Math.PI * 36 }}
                    transition={{ duration: 3, ease: "linear" }}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <motion.span
                    key={countdown}
                    initial={{ scale: 1.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="text-2xl font-black mono-num"
                    style={{ color: C.primary }}
                  >
                    {countdown}
                  </motion.span>
                </div>
              </div>
              <p className="text-xs" style={{ color: C.muted }}>
                {t("teamPage.claimCountdownHint")}
              </p>
            </motion.div>
          )}
          {step === "pending" && (
            <motion.div
              key="pending"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center py-8 gap-3"
            >
              <RefreshCw
                size={28}
                className="animate-spin"
                style={{ color: C.primary }}
              />
              <p className="text-xs" style={{ color: C.muted }}>
                {t("teamPage.claimProcessing")}
              </p>
            </motion.div>
          )}
          {step === "success" && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative flex flex-col items-center py-6 gap-4"
            >
              <ConfettiCanvas active={true} />
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 15 }}
              >
                <CheckCircle size={48} style={{ color: C.green }} />
              </motion.div>
              <div className="text-center space-y-1 relative z-10">
                <p className="font-semibold text-sm">
                  {t("teamPage.claimSuccessTitle")}
                </p>
                <p className="text-[10px]" style={{ color: C.muted }}>
                  {t("teamPage.claimSuccessDetail")}
                </p>
              </div>
              <button
                type="button"
                className="w-full py-3 rounded-lg font-bold text-xs btn-primary relative z-10"
                onClick={handleClose}
              >
                {t("teamPage.claimDone")}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}

// ===== 奖励记录弹窗（与 dapp 一致；数值为 -） =====
function RewardHistoryDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [histTab, setHistTab] = useState<"claim" | "distribution">("claim");
  const tokenUsdt = getTokenName(USDT_ADDRESS);
  const tokenTsk = getTokenName(CCC_AIF_ADDRESS);
  const tokenTskx = getTokenName(INTEGRAL_COIN_CONTRACT);
  const dash = "-";

  const amountRow = (
    <div className="grid grid-cols-3 gap-2 text-[10px]">
      <div>
        <div style={{ color: C.dim }}>{tokenUsdt}</div>
        <div className="mono-num font-semibold" style={{ color: C.green }}>
          {dash}
        </div>
      </div>
      <div>
        <div style={{ color: C.dim }}>{tokenTsk}</div>
        <div className="mono-num font-semibold" style={{ color: C.primary }}>
          {dash}
        </div>
      </div>
      <div>
        <div style={{ color: C.dim }}>{tokenTskx}</div>
        <div className="mono-num font-semibold" style={{ color: C.amber }}>
          {dash}
        </div>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-sm max-h-[80vh] overflow-y-auto"
        style={{
          background: "#0d0f14",
          border: "1px solid rgba(34,197,94,0.20)",
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: C.primary }}>
            {t("teamPage.rewardHistoryTitle")}
          </DialogTitle>
        </DialogHeader>
        <div
          className="flex gap-1 mb-4"
          style={{ borderBottom: `1px solid ${C.border}` }}
        >
          {[
            {
              id: "claim" as const,
              label: t("teamPage.rewardHistoryTabClaim"),
            },
            {
              id: "distribution" as const,
              label: t("teamPage.rewardHistoryTabDistribution"),
            },
          ].map(row => (
            <button
              type="button"
              key={row.id}
              onClick={() => setHistTab(row.id)}
              className="px-4 py-2 text-xs font-semibold"
              style={{
                color: histTab === row.id ? C.primary : C.dim,
                borderBottom:
                  histTab === row.id
                    ? `2px solid ${C.primary}`
                    : "2px solid transparent",
              }}
            >
              {row.label}
            </button>
          ))}
        </div>
        {histTab === "claim" && (
          <div className="space-y-2">
            <div
              className="rounded-lg p-3"
              style={{ background: C.card, border: `1px solid ${C.border}` }}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-semibold">{dash}</span>
                <span
                  className="text-[10px] px-2 py-0.5 rounded"
                  style={{
                    background: "rgba(34,197,94,0.08)",
                    color: C.green,
                  }}
                >
                  {t("teamPage.rewardHistoryCredited")}
                </span>
              </div>
              {amountRow}
            </div>
          </div>
        )}
        {histTab === "distribution" && (
          <div className="space-y-2">
            <div
              className="rounded-lg p-3"
              style={{ background: C.card, border: `1px solid ${C.border}` }}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-semibold">
                  {t("teamPage.rewardReferral")}
                </span>
                <span className="text-[10px]" style={{ color: C.dim }}>
                  {dash}
                </span>
              </div>
              {amountRow}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const levelColor = (level: string | number) => {
  const num =
    typeof level === "number"
      ? level
      : parseInt(String(level).replace(/\D/g, ""), 10) || 0;
  if (num >= 7) return "#f59e0b";
  if (num >= 4) return "#22c55e";
  return "#64748b";
};

// ===== 主页面 =====
export default function Team() {
  const { t } = useTranslation();
  const { userInfo } = useUserStore();
  const { data: userInviteLevelData, isLoading: isLoadingUserInviteLevel } =
    useUserInviteLevel();
  const {
    data: inviteTeamData,
    isLoading: isLoadingInviteTeam,
    isError: isInviteTeamError,
  } = useUserInviteTeam(USDT_ADDRESS);
  const { data: inviteLevelConfig, isLoading: isLoadingInviteLevelConfig } =
    useInviteLevelConfig(USDT_ADDRESS);
  const teamMembers = inviteTeamData?.items ?? [];
  const teamTotalCount = teamMembers.reduce((acc, item) => {
    return acc + (item.teamCount ?? 0);
  }, 0);

  /** 当前代币合约下的等级配置行（弹窗表格，与进度条同源） */
  const levelConfigRowsForCoin = useMemo(() => {
    const addr = USDT_ADDRESS?.trim();
    if (!addr) return [];
    const c = addr.toLowerCase();
    return [...(inviteLevelConfig?.items ?? [])]
      .filter(i => i.coinContract.toLowerCase() === c)
      .sort((a, b) => a.level - b.level);
  }, [inviteLevelConfig?.items]);

  const levelMetricsGrid = useMemo(() => {
    const lv = userInviteLevelData;
    const loadLevel = isLoadingUserInviteLevel;
    const loadTeam = isLoadingInviteTeam;
    const personalUsd = parseWeiToUsdNum(lv?.selfYj);
    const teamUsd = parseWeiToUsdNum(lv?.teamSmallYj);
    return [
      {
        id: "totalTeam",
        labelKey: "teamPage.metricTotalTeam" as const,
        value: loadTeam ? "-" : String(teamTotalCount),
        showUnit: !loadTeam,
        color: "#a78bfa",
      },
      {
        id: "direct",
        labelKey: "profilePage.referral.directReferrals" as const,
        value: loadLevel ? "-" : String(lv?.directCount ?? 0),
        showUnit: !loadLevel,
        color: C.primary,
      },
      {
        id: "valid",
        labelKey: "profilePage.referral.validAccounts" as const,
        value: loadLevel ? "-" : String(lv?.validUserCount ?? 0),
        showUnit: !loadLevel,
        color: C.amber,
      },
      {
        id: "selfYj",
        labelKey: "teamPage.metricPersonal" as const,
        value: loadLevel ? "-" : `$${formatUSDT(personalUsd)}`,
        showUnit: false,
        color: C.primary,
      },
      {
        id: "teamYj",
        labelKey: "teamPage.metricTeam" as const,
        value: loadLevel ? "-" : `$${formatNumber(teamUsd)}`,
        showUnit: false,
        color: C.amber,
      },
    ];
  }, [
    userInviteLevelData,
    isLoadingUserInviteLevel,
    isLoadingInviteTeam,
    teamTotalCount,
  ]);

  const levelProgress = useMemo(() => {
    const curLv = userInviteLevelData?.level ?? 0;
    const personal = parseWeiToUsdNum(userInviteLevelData?.selfYj);
    const team = parseWeiToUsdNum(userInviteLevelData?.teamSmallYj);
    const base = {
      personalCurrent: formatUSDT(personal),
      teamCurrent: formatNumber(team),
    };

    if (isLoadingInviteLevelConfig) {
      return {
        ...base,
        personalPct: 0,
        teamPct: 0,
        personalTarget: "-",
        teamTarget: "-",
        isMax: false as const,
        loading: true as const,
      };
    }

    const nextCfg = findNextInviteLevelConfig(
      inviteLevelConfig?.items,
      USDT_ADDRESS,
      curLv
    );

    if (!nextCfg) {
      return {
        ...base,
        personalPct: 100,
        teamPct: 100,
        personalTarget: "-",
        teamTarget: "-",
        isMax: true as const,
        loading: false as const,
      };
    }

    const targetPersonal = parseWeiToUsdNum(nextCfg.userYjAmount);
    const targetTeam = parseWeiToUsdNum(nextCfg.teamYjAmount);

    const personalPct =
      targetPersonal > 0
        ? Math.min(100, (personal / targetPersonal) * 100)
        : 100;
    const teamPct =
      targetTeam > 0 ? Math.min(100, (team / targetTeam) * 100) : 100;

    return {
      ...base,
      personalPct,
      teamPct,
      personalTarget: formatNumber(targetPersonal),
      teamTarget: targetTeam > 0 ? formatNumber(targetTeam) : "-",
      isMax: false as const,
      loading: false as const,
    };
  }, [
    userInviteLevelData,
    inviteLevelConfig?.items,
    isLoadingInviteLevelConfig,
  ]);
  const inviteCode = userInfo?.inviteCode?.trim();
  const inviteLink = inviteCode
    ? `${window.location.origin}/ic/${inviteCode}`
    : "";

  const [levelOpen, setLevelOpen] = useState(false);
  const [posterOpen, setPosterOpen] = useState(false);
  const posterRef = useRef<HTMLDivElement>(null);
  const [rewardTab, setRewardTab] = useState<"pending" | "today" | "total">(
    "pending"
  );

  // —— 团队成员表格排序：默认 null = 不排序（保持后端返回顺序）
  type MemberSortKey = "level" | "totalVolume" | "teamSize";
  const [sortKey, setSortKey] = useState<MemberSortKey | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const handleSort = (key: MemberSortKey) => {
    if (sortKey === key) {
      setSortDir(d => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setSortDir("desc"); // 首次点击：从大到小
    }
  };
  const sortedTeamMembers = useMemo(() => {
    if (!sortKey) return teamMembers;
    const getVal = (m: (typeof teamMembers)[number]) => {
      if (sortKey === "level") return Number(m.level ?? 0);
      if (sortKey === "totalVolume") {
        const n = parseFloat(formatEtherWithDecimals(m.teamTotalYj ?? 0));
        return isNaN(n) ? 0 : n;
      }
      // teamSize
      return Number(m.teamCount ?? 0);
    };
    return [...teamMembers].sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);
      return sortDir === "desc" ? bv - av : av - bv;
    });
  }, [teamMembers, sortKey, sortDir]);

  return (
    <div
      className="pt-top-header  pb-20 page-enter"
      style={{ background: C.bg }}
    >
      {/* ===== Header ===== */}
      <div
        className="px-5 pt-5 pb-4"
        style={{ background: "#0d1117", borderBottom: `1px solid ${C.border}` }}
      >
        <div className="flex items-center justify-between mb-1.5">
          <h1 className="text-xl font-bold tracking-tight">
            <span style={{ color: C.primary }}>
              {t("teamPage.headerTitleMy")}
            </span>
            <span style={{ color: C.text }}>
              {t("teamPage.headerTitleTeam")}
            </span>
          </h1>
          {/* <div className="flex items-center gap-2" style={{ color: C.muted }}>
            <Users size={14} />
            <span className="text-[9px] mono-num font-semibold">
              {t("teamPage.directReferralsBadge", {
                count: teamTotalCount,
              })}
            </span>
          </div> */}
        </div>
        {/* <p className="text-[9px]" style={{ color: C.dim }}>
          {t("teamPage.headerStatsLine", {
            direct: userInviteLevelData?.directCount ?? 0,
            valid: 0,
          })}
          <InfoTip text={t("teamPage.validAccountTip")} />
        </p> */}
      </div>

      <div className="px-4 py-5 space-y-5">
        {/* 顺序与 dapp 一致：团队收益 → 推荐链接 → 团队等级 → 团队成员 */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Gift size={13} style={{ color: C.primary }} />
            <span className="text-xs font-bold" style={{ color: C.text }}>
              {t("teamPage.sectionTeamReward")}
            </span>
          </div>
          {/* ===== Reward Card（与 dapp 布局一致；数值为 -；点击仍提示未开放） ===== */}
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.02 }}
            className="rounded-xl overflow-hidden"
            style={{ background: C.card, border: `1px solid ${C.border}` }}
          >
            <div
              className="flex"
              style={{ borderBottom: `1px solid ${C.border}` }}
            >
              {(["pending", "today", "total"] as const).map(tab => (
                <button
                  type="button"
                  key={tab}
                  onClick={() => setRewardTab(tab)}
                  className="flex-1 py-2 text-[10px] font-semibold text-center transition-all relative"
                  style={{ color: rewardTab === tab ? C.primary : C.dim }}
                >
                  {tab === "pending"
                    ? t("teamPage.rewardTabPending")
                    : tab === "today"
                      ? t("teamPage.rewardTabToday")
                      : t("teamPage.rewardTabTotal")}
                  {rewardTab === tab && (
                    <motion.div
                      layoutId="reward-tab-indicator"
                      className="absolute bottom-0 left-1/4 right-1/4 h-0.5 rounded-full"
                      style={{ background: C.primary }}
                    />
                  )}
                </button>
              ))}
            </div>

            <div className="px-3 pt-3 pb-2.5">
              <AnimatePresence mode="wait">
                <motion.div
                  key={rewardTab}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.18 }}
                >
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {[
                      {
                        token: getTokenName(USDT_ADDRESS),
                        suffix: t("teamPage.rewardTokenSuffixStable"),
                        color: C.green,
                      },
                      {
                        token: getTokenName(CCC_AIF_ADDRESS),
                        suffix: t("teamPage.rewardTokenSuffixToken"),
                        color: C.primary,
                      },
                      {
                        token: getTokenName(INTEGRAL_COIN_CONTRACT),
                        suffix: t("teamPage.rewardTokenSuffixPoints"),
                        color: C.amber,
                      },
                    ].map(item => (
                      <div
                        key={item.token}
                        className="rounded-lg py-2.5 px-2 text-center"
                        style={{
                          background: C.card2,
                          border: `1px solid ${C.border}`,
                        }}
                      >
                        <div
                          className="text-[10px] mb-1 font-medium"
                          style={{ color: C.dim }}
                        >
                          {item.token}
                          <span className="ml-1 opacity-60">
                            ({item.suffix})
                          </span>
                        </div>
                        <div
                          className="text-base font-black mono-num"
                          style={{ color: item.color }}
                        >
                          -
                        </div>
                      </div>
                    ))}
                  </div>

                  <div
                    className="rounded-lg overflow-hidden mb-2.5"
                    style={{ border: `1px solid ${C.border}` }}
                  >
                    <div
                      className="px-2.5 py-1.5"
                      style={{ background: "rgba(255,255,255,0.03)" }}
                    >
                      <span
                        className="text-[10px] font-semibold"
                        style={{ color: C.muted }}
                      >
                        {t("teamPage.rewardSourceBreakdown")}
                      </span>
                    </div>
                    {[
                      {
                        labelKey: "teamPage.rewardReferral" as const,
                        color: C.primary,
                      },
                      {
                        labelKey: "teamPage.rewardTeam" as const,
                        color: C.green,
                      },
                      {
                        labelKey: "teamPage.rewardPeer" as const,
                        color: C.amber,
                      },
                    ].map(item => (
                      <div
                        key={item.labelKey}
                        className="flex items-center px-2.5 py-2"
                        style={{ borderTop: `1px solid ${C.border}` }}
                      >
                        <div
                          className="w-1.5 h-1.5 rounded-full shrink-0 mr-2"
                          style={{ background: item.color }}
                        />
                        <div className="flex-1 min-w-0">
                          <div
                            className="text-[11px] font-semibold"
                            style={{ color: C.text }}
                          >
                            {t(item.labelKey)}
                          </div>
                        </div>
                        <span
                          className="mono-num text-xs font-bold shrink-0"
                          style={{ color: C.green }}
                        >
                          -
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {rewardTab === "pending" && (
                      <button
                        type="button"
                        className="col-span-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-[11px] font-bold btn-primary"
                        onClick={() => showInfo(t("common.comingSoon"))}
                      >
                        <Gift size={12} />
                        {t("teamPage.claimNow")}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => showInfo(t("common.comingSoon"))}
                      className={`${rewardTab === "pending" ? "col-span-1" : "col-span-2"} flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-[11px] font-semibold transition-all hover:opacity-80`}
                      style={{
                        background: C.card2,
                        border: `1px solid ${C.border}`,
                        color: C.muted,
                      }}
                    >
                      <BarChart2 size={12} />
                      {t("teamPage.rewardHistory")}
                    </button>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </div>

        {/* ===== 推荐链接：紧凑行 + 复制 + 二维码海报 + 统计（与 dapp 布局一致） ===== */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="rounded-2xl overflow-hidden"
          style={{ background: C.card, border: `1px solid ${C.border}` }}
        >
          <div className="flex items-center gap-2 px-4 pt-4 pb-2">
            <Share2
              size={12}
              className="shrink-0"
              style={{ color: C.primary }}
            />
            <span
              className="text-[10px] font-semibold truncate min-w-0"
              style={{ color: C.text }}
            >
              {t("profilePage.referral.title")}
            </span>
            {inviteCode ? (
              <button
                type="button"
                onClick={async () => {
                  const ok = await copyToClipboard(inviteCode);
                  if (ok) {
                    showSuccess(t("profilePage.referral.inviteCodeCopied"));
                  } else {
                    showError(t("profilePage.referral.copyFailed"));
                  }
                }}
                className="text-[9px] mono-num ml-auto max-w-[55%] truncate px-1.5 py-0.5 rounded-full shrink-0 font-semibold transition-opacity hover:opacity-90 active:scale-[0.98]"
                style={{
                  background: "rgba(34,197,94,0.08)",
                  color: C.primary,
                  border: "1px solid rgba(34,197,94,0.15)",
                }}
              >
                {t("profilePage.referral.inviteCode", { code: inviteCode })}
              </button>
            ) : null}
          </div>

          <div className="flex items-center gap-2 px-4 pb-2">
            <div
              className="flex-1 flex items-center min-w-0 rounded-xl px-3 py-2"
              style={{ background: C.card2, border: `1px solid ${C.border}` }}
            >
              <span
                className="flex-1 text-[9px] mono-num truncate"
                style={{ color: inviteLink ? C.muted : C.dim }}
                title={inviteLink || undefined}
              >
                {inviteLink || t("profilePage.referral.emptyLink")}
              </span>
            </div>
            <button
              type="button"
              disabled={!inviteLink}
              className="flex items-center gap-1 text-[9px] font-semibold shrink-0 px-3 py-2 rounded-xl disabled:opacity-40 disabled:pointer-events-none"
              style={{
                color: C.primary,
                background: "rgba(34,197,94,0.08)",
                border: "1px solid rgba(34,197,94,0.15)",
              }}
              onClick={async () => {
                if (!inviteLink) return;
                const ok = await copyToClipboard(inviteLink);
                if (ok) {
                  showSuccess(t("profilePage.referral.copied"));
                } else {
                  showError(t("profilePage.referral.copyFailed"));
                }
              }}
            >
              <Copy size={10} />
              {t("profilePage.referral.copy")}
            </button>
          </div>

          <div className="px-4 pb-4">
            <button
              type="button"
              disabled={!inviteLink}
              className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[10px] font-semibold transition-all hover:opacity-90 disabled:opacity-40 disabled:pointer-events-none"
              style={{
                background: "rgba(34,197,94,0.06)",
                border: "1px solid rgba(34,197,94,0.15)",
                color: C.primary,
              }}
              onClick={() => setPosterOpen(true)}
            >
              <QrCode size={12} />
              {t("profilePage.referral.generatePoster")}
            </button>
          </div>
        </motion.div>

        {/* ===== 团队等级 ===== */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Crown size={13} style={{ color: C.primary }} />
            <span className="text-xs font-bold" style={{ color: C.text }}>
              {t("teamPage.sectionTeamLevel")}
            </span>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 }}
            className="rounded-xl overflow-hidden relative"
            style={{
              background: C.card,
              border: "1px solid rgba(34,197,94,0.12)",
            }}
          >
            <div
              className="h-[2px]"
              style={{
                background:
                  "linear-gradient(90deg, transparent 5%, rgba(34,197,94,0.45) 50%, transparent 95%)",
              }}
            />

            <div className="px-3 pt-2.5 pb-2 relative z-10">
              <div className="flex items-center gap-3 mb-2">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                  style={{
                    background: "rgba(34,197,94,0.10)",
                    border: "1px solid rgba(34,197,94,0.25)",
                  }}
                >
                  <Crown size={15} style={{ color: C.primary }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span
                      className="font-black text-lg mono-num"
                      style={{ color: C.primary }}
                    >
                      C{userInviteLevelData?.level ?? 0}
                    </span>
                    <span
                      className="text-[10px] px-1.5 py-px rounded-full font-semibold"
                      style={{
                        background: "rgba(34,197,94,0.08)",
                        color: C.primary,
                        border: "1px solid rgba(34,197,94,0.20)",
                      }}
                    >
                      {tierNameForLevel(userInviteLevelData?.level ?? 0, t)}
                    </span>
                    <button
                      type="button"
                      className="ml-auto"
                      onClick={() => setLevelOpen(true)}
                      aria-label={t("teamPage.levelTableTitle")}
                    >
                      <HelpCircle
                        size={13}
                        style={{ color: C.dim }}
                        className="hover:opacity-70 transition-opacity"
                      />
                    </button>
                  </div>
                  <div
                    className="text-[10px] mt-0.5 truncate"
                    style={{ color: C.dim }}
                  >
                    {isLoadingUserInviteLevel || isLoadingInviteLevelConfig
                      ? "-"
                      : (() => {
                          const lv = userInviteLevelData?.level ?? 0;
                          const nextCfg = findNextInviteLevelConfig(
                            inviteLevelConfig?.items,
                            USDT_ADDRESS,
                            lv
                          );
                          if (!nextCfg) return t("teamPage.levelMaxReached");
                          return t("teamPage.nextLevelHint", {
                            id: `C${nextCfg.level}`,
                            name: tierNameForLevel(nextCfg.level, t),
                            personal: formatInviteLevelPersonalCell(
                              nextCfg.userYjAmount,
                              t
                            ),
                            team: formatInviteLevelTeamCell(
                              nextCfg.teamYjAmount
                            ),
                          });
                        })()}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-x-1.5 gap-y-1 mb-2">
                {levelMetricsGrid.map(item => (
                  <div
                    key={item.id}
                    className="text-center py-1.5 rounded-lg min-w-0"
                    style={{ background: "rgba(12,16,32,0.45)" }}
                  >
                    <div
                      className="text-[10px] leading-tight"
                      style={{ color: C.dim }}
                    >
                      {t(item.labelKey)}
                    </div>
                    <div
                      className="text-xs font-bold mono-num mt-0.5 wrap-break-word"
                      style={{ color: item.color }}
                    >
                      {item.value}
                      {item.showUnit ? (
                        <span
                          className="text-[10px] ml-px"
                          style={{ color: C.dim }}
                        >
                          {t("teamPage.metricUnitPeople")}
                        </span>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-1.5">
                {[
                  {
                    label: t("teamPage.metricPersonal"),
                    current: levelProgress.personalCurrent,
                    target: levelProgress.personalTarget,
                    pct: levelProgress.personalPct,
                    color: C.primary,
                    grad: `linear-gradient(90deg, ${C.primary}, #34d399)`,
                  },
                  {
                    label: t("teamPage.metricTeam"),
                    current: levelProgress.teamCurrent,
                    target: levelProgress.teamTarget,
                    pct: levelProgress.teamPct,
                    color: C.amber,
                    grad: `linear-gradient(90deg, ${C.amber}, #fbbf24)`,
                  },
                ].map(bar => (
                  <div key={bar.label}>
                    <div className="flex items-center justify-between text-[10px] mb-0.5">
                      <span style={{ color: C.dim }}>{bar.label}</span>
                      <span className="mono-num">
                        <span style={{ color: bar.color }}>${bar.current}</span>
                        <span style={{ color: C.dim }}>
                          {" "}
                          / {bar.target === "-" ? bar.target : `$${bar.target}`}
                        </span>
                      </span>
                    </div>
                    <div
                      className="h-1 rounded-full overflow-hidden"
                      style={{ background: "rgba(255,255,255,0.06)" }}
                    >
                      <motion.div
                        className="h-full rounded-full"
                        initial={{ width: 0 }}
                        animate={{
                          width: `${Math.min(bar.pct, 100)}%`,
                        }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        style={{ background: bar.grad }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* ===== Team Members Table ===== */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="rounded-2xl overflow-hidden"
          style={{ background: C.card, border: `1px solid ${C.border}` }}
        >
          <div className="px-5 pt-5 pb-3 flex items-center gap-2">
            <Users size={14} style={{ color: C.primary }} />
            <span
              className="font-semibold text-[10px]"
              style={{ color: C.text }}
            >
              {t("teamPage.membersTitle")}
            </span>
            <span
              className="text-[9px] px-2 py-0.5 rounded-full mono-num font-semibold"
              style={{
                background: "rgba(34,197,94,0.08)",
                color: C.primary,
                border: "1px solid rgba(34,197,94,0.20)",
              }}
            >
              {isLoadingInviteTeam ? "-" : teamTotalCount}
            </span>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table
              className="w-full"
              style={{ borderCollapse: "separate", borderSpacing: 0 }}
            >
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {(
                    [
                      { i18nKey: "membersTableAddress", sortKey: null },
                      {
                        i18nKey: "membersTableLevel",
                        sortKey: "level" as const,
                      },
                      {
                        i18nKey: "membersTableVolume",
                        sortKey: "totalVolume" as const,
                      },
                      {
                        i18nKey: "membersTableTeamScale",
                        sortKey: "teamSize" as const,
                      },
                    ] as Array<{
                      i18nKey: string;
                      sortKey: MemberSortKey | null;
                    }>
                  ).map(h => {
                    const isActive = h.sortKey && sortKey === h.sortKey;
                    return (
                      <th
                        key={h.i18nKey}
                        className={`text-[9px] font-semibold py-2.5  text-center whitespace-nowrap ${
                          h.sortKey
                            ? "cursor-pointer select-none hover:opacity-80 transition-opacity"
                            : ""
                        }`}
                        style={{
                          color: isActive ? C.primary : C.muted,
                          borderBottom: `1px solid ${C.border}`,
                          background: "rgba(255,255,255,0.02)",
                        }}
                        onClick={() => h.sortKey && handleSort(h.sortKey)}
                      >
                        <span className="inline-flex items-center gap-0.5">
                          {t(`teamPage.${h.i18nKey}`)}
                          {h.sortKey && (
                            <span
                              className="inline-flex flex-col leading-none"
                              style={{ fontSize: 7 }}
                            >
                              <span
                                style={{
                                  color:
                                    isActive && sortDir === "asc"
                                      ? C.primary
                                      : "rgba(255,255,255,0.2)",
                                }}
                              >
                                ▲
                              </span>
                              <span
                                style={{
                                  marginTop: -2,
                                  color:
                                    isActive && sortDir === "desc"
                                      ? C.primary
                                      : "rgba(255,255,255,0.2)",
                                }}
                              >
                                ▼
                              </span>
                            </span>
                          )}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {isLoadingInviteTeam ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="py-8 text-center text-[9px]"
                      style={{ color: C.dim }}
                    >
                      {t("teamPage.membersLoading")}
                    </td>
                  </tr>
                ) : isInviteTeamError ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="py-8 text-center text-[9px]"
                      style={{ color: C.dim }}
                    >
                      {t("teamPage.membersLoadError")}
                    </td>
                  </tr>
                ) : teamMembers.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="py-8 text-center text-[9px]"
                      style={{ color: C.dim }}
                    >
                      {t("teamPage.membersEmpty")}
                    </td>
                  </tr>
                ) : (
                  sortedTeamMembers.map((member, i) => {
                    const rowKey =
                      member.walletAddress || member.address || `row-${i}`;
                    const displayAddr = formatAddress(
                      member.walletAddress || member.address || ""
                    );
                    const totalYj = formatKMBTruncate(
                      formatEtherWithDecimals(member.teamTotalYj ?? 0)
                    );
                    const todayYj = formatKMBTruncate(
                      formatEtherWithDecimals(member.todayTeamYj ?? 0)
                    );
                    const teamCnt = member.teamCount ?? 0;
                    const todayTeamCnt = member.todayTeamCount ?? 0;
                    return (
                      <tr
                        key={rowKey}
                        style={{
                          borderBottom:
                            i < sortedTeamMembers.length - 1
                              ? `1px solid ${C.border}`
                              : "none",
                        }}
                      >
                        <td className="py-2.5 text-center">
                          <span
                            className="mono-num text-[9px]"
                            style={{ color: C.text }}
                          >
                            {displayAddr || "—"}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          <span
                            className="mono-num text-[9px] font-semibold"
                            style={{ color: levelColor(member.level) }}
                          >
                            C{member.level}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          <span
                            className="mono-num text-[9px]"
                            style={{ color: C.text }}
                          >
                            {totalYj}
                          </span>
                          <span
                            className="mono-num text-[9px] ml-1"
                            style={{ color: C.green }}
                          >
                            (+{todayYj})
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          <span
                            className="mono-num text-[9px]"
                            style={{ color: C.text }}
                          >
                            {teamCnt}
                          </span>
                          <span
                            className="mono-num text-[9px] ml-1"
                            style={{ color: C.green }}
                          >
                            (+{todayTeamCnt})
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>

      <Dialog open={posterOpen} onOpenChange={setPosterOpen}>
        <DialogContent
          className="max-w-sm p-0 overflow-hidden"
          style={{ background: C.bg, border: `1px solid ${C.border}` }}
        >
          <DialogHeader className="px-4 pt-4 pb-2">
            <DialogTitle
              className="text-sm font-bold"
              style={{ color: C.text }}
            >
              {t("profilePage.referral.posterTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="px-4 pb-3">
            <div
              ref={posterRef}
              className="rounded-xl overflow-hidden"
              style={{
                background:
                  "linear-gradient(145deg, #0f1117 0%, #0a1a0f 50%, #0f1117 100%)",
                border: `1px solid ${C.border}`,
              }}
            >
              <div className="px-5 pt-5 pb-4">
                <div className="flex items-center gap-2 mb-4">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{
                      background: "rgba(34,197,94,0.12)",
                      border: "1px solid rgba(34,197,94,0.25)",
                    }}
                  >
                    <span
                      className="text-sm font-black"
                      style={{ color: C.primary }}
                    >
                      FT
                    </span>
                  </div>
                  <div>
                    <div
                      className="text-sm font-bold"
                      style={{ color: C.text }}
                    >
                      FortTrade
                    </div>
                    <div className="text-[9px]" style={{ color: C.dim }}>
                      {t("profilePage.referral.posterBrandSubtitle")}
                    </div>
                  </div>
                </div>

                {inviteCode ? (
                  <div className="text-center mb-4">
                    <div className="text-[10px] mb-1" style={{ color: C.dim }}>
                      {t("profilePage.referral.posterInviteCodeLabel")}
                    </div>
                    <div
                      className="text-2xl font-black mono-num tracking-widest"
                      style={{ color: C.primary }}
                    >
                      {inviteCode.toUpperCase()}
                    </div>
                  </div>
                ) : null}

                {inviteLink ? (
                  <>
                    <div className="flex justify-center mb-4">
                      <div
                        className="p-3 rounded-xl"
                        style={{ background: "#ffffff" }}
                      >
                        <QRCodeSVG
                          value={inviteLink}
                          size={140}
                          level="H"
                          bgColor="#ffffff"
                          fgColor="#0a0b0e"
                        />
                      </div>
                    </div>
                    <div className="text-center">
                      <div
                        className="text-[9px] mb-0.5"
                        style={{ color: C.dim }}
                      >
                        {t("profilePage.referral.posterScanHint")}
                      </div>
                      <div
                        className="text-[10px] mono-num wrap-break-word px-1"
                        style={{ color: C.muted }}
                      >
                        {inviteLink}
                      </div>
                    </div>
                  </>
                ) : null}

                <div className="grid grid-cols-3 gap-2 mt-4">
                  {(
                    [
                      ["posterCustodyLabel", "posterCustodyValue"],
                      ["posterRewardLabel", "posterRewardValue"],
                      ["posterChainLabel", "posterChainValue"],
                    ] as const
                  ).map(([lk, vk]) => (
                    <div
                      key={lk}
                      className="text-center py-1.5 rounded-lg"
                      style={{
                        background: "rgba(34,197,94,0.06)",
                        border: "1px solid rgba(34,197,94,0.12)",
                      }}
                    >
                      <div className="text-[9px]" style={{ color: C.dim }}>
                        {t(`profilePage.referral.${lk}`)}
                      </div>
                      <div
                        className="text-[10px] font-bold"
                        style={{ color: C.primary }}
                      >
                        {t(`profilePage.referral.${vk}`)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <button
              type="button"
              className="w-full mt-3 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-bold btn-primary"
              onClick={async () => {
                if (!posterRef.current) return;
                try {
                  const canvas = await html2canvas(posterRef.current, {
                    backgroundColor: "#0a0b0e",
                    scale: 3,
                    useCORS: true,
                  });
                  const link = document.createElement("a");
                  const code = inviteCode?.toUpperCase() ?? "POSTER";
                  link.download = `FortTrade-${code}.png`;
                  link.href = canvas.toDataURL("image/png");
                  link.click();
                  showSuccess(t("profilePage.referral.posterSaved"));
                } catch {
                  showError(t("profilePage.referral.posterSaveFailed"));
                }
              }}
            >
              <Download size={14} />
              {t("profilePage.referral.savePoster")}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <LevelInfoDialog
        open={levelOpen}
        onClose={() => setLevelOpen(false)}
        currentLevel={userInviteLevelData?.level ?? 0}
        rows={levelConfigRowsForCoin}
        isLoading={isLoadingInviteLevelConfig}
      />
      <ClaimRewardDialog open={false} onClose={() => {}} />
      <RewardHistoryDialog open={false} onClose={() => {}} />
    </div>
  );
}
